// What a workflow stage shows, on the staff client record and in the client
// portal, from one stored configuration.
//
// A client in onboarding has nothing to say about Clifton Strengths yet, so
// showing the panel invites staff to fill in something the engagement has not
// reached, and opening a North Star page in the portal promises the client a
// document nobody has written. Which parts a stage shows is therefore admin
// configuration, edited in Stage Gates and stored on the workflow stage
// definition as `hiddenPanels`. One list governs both surfaces so the record
// and the portal cannot drift apart.
//
// The catalogue lives here, on the frontend, on purpose: a panel is a piece of
// UI, and the backend has no business knowing the record's layout. The lambda
// validates only the shape of what it stores (an array of short slugs), and the
// Stage Gates editor offers checkboxes rather than free text, so an id that is
// not in this list cannot be entered in the first place.
//
// `hiddenPanels` is a DENY list, and that direction matters: a stage with no
// configuration — most stages, most of the time — shows everything. An allow
// list would have made an empty array mean "hide everything".
//
// A second rule applies to the portal alone: a page staff provision and the
// client cannot fill in hides itself while it is still empty. It is portal-only
// because on the record it would bite its own tail — the North Star section is
// where staff go to create the North Star.
//
// A portal path this catalogue does not mention is not gated at all. That is
// the Dashboard's exemption, and it means a new portal page ships visible until
// someone adds it here on purpose.

/** The top-level sections of the record, in the order they are rendered. */
export type ClientRecordSectionId =
  | 'stage'
  | 'profile'
  | 'resume'
  | 'linkedin'
  | 'north-star'
  | 'activity'
  | 'files'

/** The record's own heading for each section, for the Stage Gates captions. */
export const RECORD_SECTION_LABELS: Record<ClientRecordSectionId, string> = {
  stage: 'Stage',
  profile: 'Profile',
  resume: 'Resume',
  linkedin: 'LinkedIn',
  'north-star': 'North Star',
  activity: 'Activity',
  files: 'Files',
}

/**
 * Whether the content behind the three staff-provisioned portal pages exists
 * yet. Computed by `get-portal-dashboard`, which runs the same queries the
 * pages themselves render from.
 */
export interface PortalContentAvailability {
  northStar: boolean
  resume: boolean
  linkedin: boolean
}

export interface StageVisibilityEntry {
  id: string
  label: string
  /** The record section this entry renders inside, where the record has one. */
  section?: ClientRecordSectionId
  /** The portal route this entry governs, where the portal has one. */
  portalPath?: string
  /** Entries that are one tab of `portalPath` rather than the whole page. */
  portalTab?: boolean
  /**
   * Further portal routes this entry governs. Used where a page is a sibling of
   * another rather than a part of it, and should open and close with it: the
   * Signals page is gated by the Front Door entry, so there is no second
   * checkbox to forget when a stage hides the Front Door.
   */
  portalAlsoPaths?: readonly string[]
  /** Set only on pages staff provision and the client cannot fill in. */
  portalContentKey?: keyof PortalContentAvailability
  /** The portal's own name for this entry, where it differs from `label`. */
  portalLabel?: string
  /**
   * Panels that cannot be hidden. Stage carries where the client is and how to
   * advance them, which is never irrelevant, and leaving one panel unhideable
   * means an admin cannot configure the record down to a blank page.
   */
  alwaysVisible?: boolean
}

export const STAGE_VISIBILITY_ENTRIES: readonly StageVisibilityEntry[] = [
  { id: 'stage', label: 'Stage', section: 'stage', alwaysVisible: true },
  {
    id: 'clifton-strengths',
    label: 'Clifton Strengths',
    section: 'profile',
    portalPath: '/portal/profile',
    portalTab: true,
  },
  {
    id: 'dream-job',
    label: 'Dream Job',
    section: 'profile',
    portalPath: '/portal/profile',
    portalTab: true,
    portalLabel: 'Northstar Companies',
  },
  {
    id: 'resume-old',
    label: 'Old Resume',
    section: 'resume',
    portalPath: '/portal/profile',
    portalTab: true,
  },
  {
    id: 'resume-new',
    label: 'New Resume',
    section: 'resume',
    portalPath: '/portal/profile',
    portalTab: true,
    portalContentKey: 'resume',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn Content',
    section: 'linkedin',
    portalPath: '/portal/profile',
    portalTab: true,
    portalContentKey: 'linkedin',
    portalLabel: 'LinkedIn',
  },
  {
    id: 'north-star',
    label: 'North Star',
    section: 'north-star',
    portalPath: '/portal/profile',
    portalTab: true,
    portalContentKey: 'northStar',
  },
  { id: 'tasks', label: 'Tasks', section: 'activity', portalPath: '/portal/profile', portalTab: true },
  { id: 'comments', label: 'Comments', section: 'activity' },
  {
    id: 'client-folder',
    label: 'Client Folder',
    section: 'files',
    portalPath: '/portal/profile',
    portalTab: true,
    portalLabel: 'Documents',
  },
  {
    id: 'stars-and-stripes',
    label: 'Stars and Stripes',
    section: 'profile',
    portalPath: '/portal/profile',
    portalTab: true,
  },
  { id: 'networking', label: 'Side Door', portalPath: '/portal/networking' },
  // MWA-421 — the Signals page rides on the Front Door entry. It lists hidden
  // opportunities, which used to be a Front Door tab, so a stage that hides the
  // Front Door hides them too.
  {
    id: 'job-applications',
    label: 'Front Door',
    portalPath: '/portal/opportunities',
    portalAlsoPaths: ['/portal/signals'],
    portalLabel: 'Front Door and Signals',
  },
]

/** The entries an admin may hide — everything except the protected ones. */
export const GATEABLE_STAGE_VISIBILITY_ENTRIES = STAGE_VISIBILITY_ENTRIES.filter(
  (entry) => !entry.alwaysVisible
)

/**
 * Reads a stage's `hiddenPanels` into a predicate. An unknown or absent
 * configuration shows everything, and a protected panel is always shown even if
 * it somehow appears in the stored list.
 */
export function buildPanelVisibility(hiddenPanels?: string[] | null) {
  const hidden = new Set(Array.isArray(hiddenPanels) ? hiddenPanels : [])

  const isPanelVisible = (panelId: string): boolean => {
    const panel = STAGE_VISIBILITY_ENTRIES.find((p) => p.id === panelId)
    if (panel?.alwaysVisible) return true
    return !hidden.has(panelId)
  }

  // A section whose every panel is hidden should not render an empty heading,
  // and should leave the navigation spine with it.
  const isSectionVisible = (sectionId: ClientRecordSectionId): boolean =>
    STAGE_VISIBILITY_ENTRIES.filter((p) => p.section === sectionId).some((p) =>
      isPanelVisible(p.id)
    )

  return { isPanelVisible, isSectionVisible }
}

/**
 * Portal paths are compared as strings, so `/portal/resume/` and
 * `/portal/resume` have to arrive here the same. An empty path is the portal
 * root, which is the Dashboard.
 */
export function normalizePortalPath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '')
  return trimmed === '' ? '/portal' : trimmed
}

/**
 * The portal's half of the same configuration, plus the content rule. Both
 * arguments fail toward showing more: no stage configuration hides nothing, and
 * `content: null` — a dashboard fetch that did not land — skips the content
 * rule rather than guessing a page is empty.
 */
export function buildPortalVisibility(
  hiddenPanels: string[] | null | undefined,
  content: PortalContentAvailability | null
) {
  const hidden = new Set(Array.isArray(hiddenPanels) ? hiddenPanels : [])

  const isEntryVisible = (id: string): boolean => {
    const entry = STAGE_VISIBILITY_ENTRIES.find((e) => e.id === id)
    if (!entry) return true
    if (entry.alwaysVisible) return true
    if (hidden.has(id)) return false
    if (entry.portalContentKey && content !== null && content[entry.portalContentKey] === false) {
      return false
    }
    return true
  }

  // A page with no entry against it is ungated, which is how the Dashboard
  // stays reachable without a flag anyone could tick. A page whose entries are
  // all hidden goes; one visible tab is enough to keep its page.
  const isPathVisible = (path: string): boolean => {
    const normalized = normalizePortalPath(path)
    const entries = STAGE_VISIBILITY_ENTRIES.filter(
      (e) => e.portalPath === normalized || e.portalAlsoPaths?.includes(normalized)
    )
    if (entries.length === 0) return true
    return entries.some((e) => isEntryVisible(e.id))
  }

  return { isEntryVisible, isPathVisible }
}
